import React, { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import frLocale from "date-fns/locale/fr";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CheckIcon, XCircleIcon } from "@phosphor-icons/react";
import CustomSearchInput from "../../../ui/CustomSearchInput";
import TaskOption from "../TaskOption";
import { getEntryId, createTaskFilter, TIMEZONE } from "../utils/trackingUtils";

const TimeTableEditRow = ({ entry, projects, tasks, sections, localComments, onEdit, onCommentsChange, onSubmit, onCancel, onDelete, onKeyDown, tab, activeDay, setActiveDay }) => {
    const entryId = getEntryId(entry);

    const taskFilter = useMemo(() => createTaskFilter(sections, tasks), [sections, tasks]);

    const filteredTasks = useMemo(() => {
        if (!entry.projectId) return [];
        const filtered = tasks.filter((t) => t.__isSeparator || sections.some((s) => s.projectId === entry.projectId && s.id === t.sectionId));
        const separatorIndex = filtered.findIndex((t) => t.__isSeparator);
        if (separatorIndex === 0 || separatorIndex === filtered.length - 1) {
            return filtered.filter((t) => !t.__isSeparator);
        }
        return filtered;
    }, [entry.projectId, tasks, sections]);

    const handleDateChange = useCallback(
        (newValue) => {
            const newDate = newValue.tz(TIMEZONE).format("YYYY-MM-DD");
            onEdit(entryId, { ...entry, date: newDate });

            if (tab === 0 && setActiveDay && activeDay) {
                const newDateObj = dayjs(newDate);
                const currentWeekStart = activeDay.startOf("isoWeek");
                const currentWeekEnd = activeDay.endOf("isoWeek");

                if (newDateObj.isBefore(currentWeekStart) || newDateObj.isAfter(currentWeekEnd)) {
                    setActiveDay(newDateObj);
                } else if (newDateObj.isoWeekday() !== activeDay.isoWeekday()) {
                    setActiveDay(activeDay.isoWeekday(newDateObj.isoWeekday()));
                }
            }
        },
        [entry, entryId, onEdit, tab, activeDay, setActiveDay]
    );

    const handleProjectChange = useCallback(
        (value) => {
            onEdit(entryId, { ...entry, projectId: value ? value.id : null, taskId: null });
        },
        [entry, entryId, onEdit]
    );

    const handleTaskChange = useCallback(
        (value) => {
            onEdit(entryId, { ...entry, taskId: value ? value.id : null });
        },
        [entry, entryId, onEdit]
    );

    const handleDurationChange = useCallback(
        (event) => {
            const value = event.target.value;
            if (!/^[0-9]*[.,]?[0-9]*$/.test(value)) return;

            if (value.endsWith(".") || value.endsWith(",")) {
                onEdit(entryId, { ...entry, duration: value });
            } else {
                const normalized = value.replace(",", ".");
                const parsed = parseFloat(normalized);
                onEdit(entryId, {
                    ...entry,
                    duration: value === "" ? "" : !isNaN(parsed) ? parsed : value,
                });
            }
        },
        [entry, entryId, onEdit]
    );

    const handleCancelOrDelete = useCallback(() => {
        if (entry.tempId) {
            onDelete(entry);
        } else {
            onCancel(entry.id);
        }
    }, [entry, onDelete, onCancel]);

    return (
        <>
            <td className="entry-line-date editable">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={frLocale}>
                    <DatePicker
                        value={dayjs(entry.date)}
                        onChange={handleDateChange}
                        format="DD/MM/YYYY"
                        className="w-full"
                        slotProps={{
                            textField: {
                                placeholder: "",
                                sx: {
                                    "& .MuiInputBase-root": { height: "40px" },
                                    "& .MuiOutlinedInput-root": { height: "40px" },
                                    "& .MuiInputLabel-root": {
                                        transform: "translate(14px, 8px) scale(1)",
                                        "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" },
                                    },
                                    "& .MuiInputBase-input": { cursor: "pointer" },
                                },
                                onKeyDown: (e) => onKeyDown(e, entry),
                            },
                        }}
                    />
                </LocalizationProvider>
            </td>
            <td className="entry-line-project">
                <CustomSearchInput
                    isClearable
                    entity={projects}
                    labelType="project"
                    value={projects.find((p) => p.id === entry.projectId)}
                    onChange={handleProjectChange}
                    placeholder="Rechercher"
                    className="entry-line-select"
                />
            </td>
            <td className="entry-line-task">
                <CustomSearchInput
                    entity={filteredTasks}
                    value={entry.taskId ? tasks.find((t) => t.id === entry.taskId) : null}
                    onChange={handleTaskChange}
                    className="entry-line-select"
                    placeholder="Rechercher"
                    isClearable
                    Option={(props) => <TaskOption {...props} tasks={tasks} sections={sections} />}
                    filterOption={taskFilter}
                />
            </td>
            <td className="entry-line-comments">
                <input
                    type="text"
                    className="classic-input comment-input"
                    value={localComments[entryId] ?? entry.comments ?? ""}
                    onChange={(e) => onCommentsChange(entryId, e.target.value)}
                    onKeyDown={(e) => onKeyDown(e, entry)}
                />
            </td>
            <td className="entry-line-duration">
                <input type="text" className="classic-input number-input" value={entry.duration} onChange={handleDurationChange} onKeyDown={(e) => onKeyDown(e, entry)} />
            </td>
            <td className="entry-line-actions">
                <div className="action-buttons-container">
                    <button className="action-button save-button" onClick={() => onSubmit(entry)} title="Valider">
                        <CheckIcon size={16} weight="bold" />
                    </button>
                    <button className="action-button delete-button" onClick={handleCancelOrDelete} title="Annuler">
                        <XCircleIcon size={16} weight="bold" />
                    </button>
                </div>
            </td>
        </>
    );
};

export default React.memo(TimeTableEditRow);
